import { ReactNode } from "react";

import styled from "styled-components";

import { ColorAA } from "../constants";
import TooltipProvider from "../TooltipProvider";

const BreakdownBarContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 1px;
`;

const BreakdownSectionContainer = styled.div<{ $size: number; color?: string }>`
  flex: ${(props) => props.$size};
  min-width: 5px;
  text-wrap: nowrap;
  &:last-child {
    text-align: right;
  }
`;

const BarSegmentLabel = styled.div`
  color: var(--aa-text-muted);
`;

const BarSegmentWrapper = styled.div`
  height: 30px;
  display: block;
  position: relative;
`;

const BarSegment = styled.div<{
  $stripePattern?: boolean;
  $borderColor?: string;
  $fillColor?: string;
  $disabled?: boolean;
}>`
  border-radius: 3px;
  border: ${({ $borderColor }) => $borderColor} solid 2px;
  width: 100%;
  height: 80%;
  transition: height 0.2s;
  position: absolute;
  bottom: 0;

  ${({ $disabled }) =>
    $disabled
      ? ``
      : `&:hover {
    height: 100%;
  }`}

  ${({ $stripePattern, $borderColor, $fillColor }) =>
    $stripePattern
      ? `
      border: ${$borderColor} solid 2px;
    `
      : `
      background-color: ${$fillColor};
    `}
`;

// Shades the leading part of a segment, e.g. the share of a total that is a
// sub-quantity of it.
const BarSegmentOverlay = styled.div<{ $fraction: number; $color?: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $fraction }) => $fraction * 100}%;
  background-color: ${({ $color }) => $color};
  opacity: 0.5;
`;

type Segment = {
  size: number;
  // Fraction of the segment, from 0 to 1, to shade.
  overlay?: number;
  borderColor?: string;
  fillColor?: string;
  stripePattern?: boolean;
  label?: string;
  tooltip?: ReactNode;
};

const DEFAULT_COLORS = [
  ColorAA.violet,
  ColorAA.turquoise,
  ColorAA.yellow,
  ColorAA.blue,
];

const EmptySection = () => {
  return (
    <BreakdownSectionContainer $size={100}>
      <BarSegmentWrapper>
        <BarSegment
          $disabled={true}
          $borderColor={ColorAA.lightGrey}
          $fillColor={ColorAA.lightGrey}
        />
      </BarSegmentWrapper>
    </BreakdownSectionContainer>
  );
};

const BreakdownSection = ({
  segment,
  defaultColor,
}: {
  segment: Segment;
  defaultColor?: string;
}) => {
  return (
    <BreakdownSectionContainer $size={segment.size}>
      <TooltipProvider tooltip={segment.tooltip}>
        <BarSegmentWrapper>
          <BarSegment
            $stripePattern={segment.stripePattern}
            $borderColor={segment.borderColor || defaultColor}
            $fillColor={segment.fillColor || defaultColor}
          >
            {!!segment.overlay && (
              <BarSegmentOverlay
                data-testid="bar-segment-overlay"
                $fraction={Math.min(Math.max(segment.overlay, 0), 1)}
                $color={segment.borderColor || defaultColor}
              />
            )}
          </BarSegment>
        </BarSegmentWrapper>
      </TooltipProvider>
      <BarSegmentLabel>{segment.label}</BarSegmentLabel>
    </BreakdownSectionContainer>
  );
};

const BreakdownBar = ({ segments }: { segments: Array<Segment> }) => {
  return (
    <BreakdownBarContainer>
      {segments.length === 0 && <EmptySection />}
      {segments.map((segment, index) => (
        <BreakdownSection
          key={`segment-${index}`}
          segment={segment}
          defaultColor={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
        />
      ))}
    </BreakdownBarContainer>
  );
};

export default Object.assign(BreakdownBar, {
  DefaultColors: DEFAULT_COLORS,
});
